import React from 'react';
import { SubSidebar } from 'components/SubSidebar';
import { useTranslation } from 'react-i18next';
import tw from 'twin.macro';
import { Divider } from 'components/Divider';
import FormGroup from 'components/Form/FormGroup';
import { SwitchToggle } from 'components/Form/SwitchToggle';
import {
  useProg,
  useProgView,
} from 'containers/Programmations/atoms/progAtoms';
import { useNbOfMatieres } from 'containers/Programmations/atoms/matieresAtoms';
import { ButtonToggle } from '../Preview/Sidebar/ButtonToggle';
import { SelectPrintView } from '../Preview/Sidebar/SelectPrintView';
import { MIN_NB_OF_WEEKS_TO_GROUP_WEEKS } from 'containers/Programmations/ProgV2/constants';
import Button from 'components/Buttons/Button';
import { DuplicateIcon } from 'components/Icons/icons/Duplicate';
import { PrintConfig } from 'containers/Programmations/ProgV2/Preview/types';

interface Props {
  printConfig: PrintConfig;
  onUpdatePrintConfig: (v: PrintConfig) => void;
  token: string;
}

export function Sidebar(props: Props) {
  const { printConfig, onUpdatePrintConfig } = props;
  const { t } = useTranslation(['common', 'programmation']);
  const prog = useProg();
  const progView = useProgView();
  const nbOfMatieres = useNbOfMatieres() || 0;

  if (!prog) {
    return null;
  }

  const isMatiereEmpty = nbOfMatieres === 0;
  const progMatieres = prog?.matieres || [];
  const isCompatiblePeriodeSemaine = progView === 'week';
  const isUnified = printConfig.view.includes('matiere');

  return (
    <SubSidebar
      order={0}
      collapsedSize={3}
      minSize={12}
      dynamicStyle={({ isExpanded }) => [isExpanded && tw`min-w-[270px]`]}
      collapsedComponent={<div />}
    >
      <div tw="px-4 py-5">
        {isMatiereEmpty ? null : (
          <>
            <Button
              fill
              large
              intent="primary"
              icon={DuplicateIcon as any}
              onClick={() => {
                window.location.href = `/inscription?share-token=${props.token}&share-entity-id=${prog.id}&share-entity-type=programmation`;
              }}
            >
              {t('common:buttons.copyAndModify')}
            </Button>
            <Divider tw="my-5" />
            {progMatieres.length > 1 && (
              <>
                <FormGroup label={t('programmation:displayedMatieres')}>
                  <ButtonToggle
                    fields={progMatieres.map(m => ({
                      value: m.id,
                      label: m.name,
                    }))}
                    values={printConfig.matiereIds}
                    onChange={values =>
                      onUpdatePrintConfig({
                        ...printConfig,
                        matiereIds: values,
                      })
                    }
                  />
                </FormGroup>
                <Divider tw="my-5" />
              </>
            )}
            <div tw="mb-2">
              <SelectPrintView
                onUpdatePrintConfig={onUpdatePrintConfig}
                printConfig={printConfig}
                mode={isUnified ? 'unified' : 'classic'}
                isProgression={progView === 'week'}
              />
              {/* en partage on n'a pas la matiereId dans l'url, on ne peut pas distinguer le mode classic ou unified */}
            </div>

            {isCompatiblePeriodeSemaine &&
              (prog?.periodes || []).length > MIN_NB_OF_WEEKS_TO_GROUP_WEEKS &&
              (printConfig.view === 'periode_x_domaine' ||
                printConfig.view === 'domaine_x_periode') && (
                <div tw="mb-2">
                  <SwitchToggle
                    checked={printConfig.groupWeeksByPeriodes}
                    onChange={value =>
                      onUpdatePrintConfig({
                        ...printConfig,
                        groupWeeksByPeriodes: value,
                      })
                    }
                    label={t('programmation:groupWeeksByPeriodes')}
                  />
                </div>
              )}
            {printConfig.view !== 'periode_x_semaine' && (
              <div tw="mb-2">
                <SwitchToggle
                  checked={printConfig.rowTitleOrientation === 'horizontal'}
                  onChange={value =>
                    onUpdatePrintConfig({
                      ...printConfig,
                      rowTitleOrientation: value ? 'horizontal' : 'vertical',
                    })
                  }
                  label={t('programmation:rowTitleHorizontal')}
                />
              </div>
            )}
          </>
        )}
      </div>
    </SubSidebar>
  );
}
